import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

const LivingBackground = () => {
    const { isExploding } = useCart();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Detect if mobile on mount and resize
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Don't render on mobile for performance
    if (isMobile) return null;

    return (
        <>
            {/* === ARRIÈRE-PLAN VIVANT (Z-INDEX BAS) === */}
            <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
                {/* Gradient de base */}
                <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />

                {/* Blobs animés géants */}
                <motion.div
                    className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full blur-[150px] opacity-30"
                    style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
                    animate={{
                        x: isExploding ? [0, 100, -100, 0] : [-100, 100, -100],
                        y: isExploding ? [0, -150, 150, 0] : [-100, 150, -100],
                        scale: isExploding ? [1, 1.8, 0.8, 1] : [1, 1.2, 1],
                        opacity: isExploding ? [0.3, 0.7, 0.3] : [0.3]
                    }}
                    transition={{
                        duration: isExploding ? 2 : 20,
                        repeat: isExploding ? 0 : Infinity,
                        ease: "easeInOut"
                    }}
                />

                <motion.div
                    className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full blur-[120px] opacity-25"
                    style={{ background: 'radial-gradient(circle, #9333EA 0%, transparent 70%)' }}
                    animate={{
                        x: isExploding ? [0, -150, 150, 0] : [100, -50, 100],
                        y: isExploding ? [0, 150, -100, 0] : [0, 100, 0],
                        scale: isExploding ? [1, 2, 0.5, 1] : [1, 1.3, 1],
                        opacity: isExploding ? [0.25, 0.8, 0.25] : [0.25]
                    }}
                    transition={{
                        duration: isExploding ? 2 : 18,
                        repeat: isExploding ? 0 : Infinity,
                        ease: "easeInOut",
                        delay: isExploding ? 0.1 : 1
                    }}
                />

                <motion.div
                    className="absolute bottom-0 left-1/3 w-[700px] h-[700px] rounded-full blur-[180px] opacity-20"
                    style={{ background: 'radial-gradient(circle, #EC4899 0%, transparent 70%)' }}
                    animate={{
                        x: isExploding ? [0, 100, -100, 0] : [-50, 50, -50],
                        y: isExploding ? [0, -200, 100, 0] : [50, -50, 50],
                        scale: isExploding ? [1, 1.5, 0.8, 1] : [1, 1.1, 1],
                        opacity: isExploding ? [0.2, 0.6, 0.2] : [0.2]
                    }}
                    transition={{
                        duration: isExploding ? 2 : 25,
                        repeat: isExploding ? 0 : Infinity,
                        ease: "easeInOut",
                        delay: isExploding ? 0.2 : 2
                    }}
                />

                {/* Particules flottantes - reduced from 30 to 8 for performance */}
                {[...Array(8)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-white rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            y: isExploding ? [0, -100, 0] : [0, -30, 0],
                            x: isExploding ? [0, Math.random() * 100 - 50, 0] : [0, Math.random() * 20 - 10, 0],
                            opacity: isExploding ? [0.2, 1, 0.2] : [0.2, 0.8, 0.2],
                            scale: isExploding ? [1, 3, 1] : [1, 1.5, 1]
                        }}
                        transition={{
                            duration: isExploding ? 1.5 : (3 + Math.random() * 4),
                            repeat: Infinity,
                            delay: Math.random() * 5,
                            ease: "easeInOut"
                        }}
                    />
                ))}

                {/* Lignes de grille subtiles */}
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: `
                        linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                    `,
                        backgroundSize: '50px 50px'
                    }}
                />

                {/* Overlay de texture */}
                <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' /%3E%3C/svg%3E")'
                    }}
                />
            </div>

            {/* === EFFETS MAGIQUES (Z-INDEX HAUT) === */}
            <div className="fixed inset-0 z-[100] pointer-events-none">
                {/* Flash d'écran */}
                <AnimatePresence>
                    {isExploding && (
                        <motion.div
                            className="absolute inset-0"
                            initial={{ opacity: 0 }}
                            animate={{
                                opacity: [0, 0.3, 0],
                                background: [
                                    'radial-gradient(circle, rgba(0,240,255,0.5) 0%, transparent 50%)',
                                    'radial-gradient(circle, rgba(147,51,234,0.5) 0%, transparent 50%)',
                                    'radial-gradient(circle, rgba(236,72,153,0.5) 0%, transparent 50%)'
                                ]
                            }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1 }}
                        />
                    )}
                </AnimatePresence>

                {/* Explosion de particules arc-en-ciel */}
                <AnimatePresence>
                    {isExploding && [...Array(50)].map((_, i) => {
                        const angle = (i / 50) * Math.PI * 2;
                        const distance = 200 + Math.random() * 300;
                        const x = Math.cos(angle) * distance;
                        const y = Math.sin(angle) * distance;

                        return (
                            <motion.div
                                key={`explosion-${i}`}
                                className="absolute w-2 h-2 rounded-full"
                                style={{
                                    left: '50%',
                                    top: '50%',
                                    background: `hsl(${i * 7}, 100%, 60%)`
                                }}
                                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                                animate={{
                                    x: x,
                                    y: y,
                                    opacity: 0,
                                    scale: [1, 2, 0]
                                }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: 1.5,
                                    ease: "easeOut",
                                    delay: i * 0.01
                                }}
                            />
                        );
                    })}
                </AnimatePresence>
            </div>
        </>
    );
};

export default LivingBackground;
